// @flow
import React, { useState, useRef, useEffect } from 'react';
import { Pill } from '@finect/tabular-components/Pills';
import { getSuperPartnerBaseURL } from '@finect/front-utils/env';
import { Wrapper, Placeholder } from './partials';

type Props = {
  id?: string,
  height?: number
};

const PILL_HEIGHT = 600;

/**
 * Píldora de captación de lead embebida (inline) con carga diferida.
 *
 * Monta el iframe de `advisers-leads` solo cuando el bloque está a punto de entrar en el
 * viewport (IntersectionObserver) para no penalizar LCP/INP en la carga inicial del artículo.
 * Reserva la altura con un placeholder y usa `growOnResize={false}` (altura fija) para que no
 * haya CLS. El `id` permite que los CTA intermedios hagan scroll hasta aquí.
 *
 * @param id      id del nodo (destino del scroll de los CTA intermedios).
 * @param height  altura reservada y del iframe, en px.
 */
export const LazyPill = ({ id = 'solicita-asesor', height = PILL_HEIGHT }: Props) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !ref.current) return undefined;

    // Navegadores sin IntersectionObserver: cargar directamente.
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some(entry => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '600px 0px' }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const pill = `${getSuperPartnerBaseURL()}/pills/es_ES/finect/default/advisers-leads/steps/advisoryObjective`;

  return (
    <Wrapper id={id} ref={ref} $height={height}>
      {visible ? (
        <Pill pill={pill} height={height} growOnResize={false} onLogin={() => window.postMessage({ status: 'loggedIn' })} />
      ) : (
        <Placeholder $height={height} aria-hidden="true" />
      )}
    </Wrapper>
  );
};
